import {
  Alert,
  Box,
  Button,
  Container,
  DialogContent,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { guardianSchema } from 'utils/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { GaurdianRelation } from 'utils/dropDownList';
import { useAppSelector } from 'store/hooks';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { toast } from 'react-toastify';
import ReactPhoneInput from 'react-phone-input-material-ui';
import { useAuth } from 'utils/AuthContext';
import { StyledDialog } from 'theme/index';

//PrimaryGaurdian component
const PrimaryGaurdian = (props: any) => {
  //validation schema PG
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    getValues,
    setValue,
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(guardianSchema),
    mode: 'onChange',
  });
  const [disableField, setDisableField] = useState(false);
  const [disableRelation, setDisableRelation] = useState(false);
  const [nextButton, setNextButton] = useState(true);
  //states  for PG
  const guardians = useAppSelector((state) => state.user.guardians);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [readOnlyWarningDialog, setReadOnlyWarningDialog] = useState<boolean>(false);
  const isProfileDetailsFetched = useAppSelector(
    (state) => state.user.loaders.isProfileDetailsFetched
  );

  const { user } = useAuth();
  const isAdmin = user?.isAdmin;

  const setUserPermission = (id: BigInteger, relation: string) => {
    const validValues = ['Father', 'Mother', 'Guardian'];
    if (isAdmin) {
      if (user?.permissions?.includes('GLOBAL_EDIT_PROFILE')) {
        setDisableField(false);
        setDisableRelation(false);
      } else if (user?.permissions?.includes('EDIT_PROFILE') && id !== undefined) {
        setDisableField(true);
        setDisableRelation(true);
      }
    } else if (user?.permissions?.includes('EDIT_PROFILE') && id !== undefined) {
      setDisableField(true);
      setDisableRelation(validValues.includes(relation));
    }
    if (id !== undefined) {
      setNextButton(false);
    }
  };

  const validRelations = ['Mother', 'Father', 'Guardian'];
  const getRelation = validRelations.includes(user?.relation)
    ? user?.relation
    : '';
  const setPrimaryGuardianDetails = () => {
    if (props?.primaryGaurdian) {
      setUserPermission(props?.primaryGaurdian.id, props?.primaryGaurdian.relation);
      Object.keys(props?.primaryGaurdian).forEach((key: any) => {
        if (key === 'other' && props?.primaryGaurdian['relation'] !== 'other')
          setValue(key, '', { shouldValidate: true, shouldDirty: false });
        else if (key === 'mobileNumber') {
          const str = props?.primaryGaurdian?.mobileNumber.replaceAll(' ', '');
          const mobile = str.replace(/[^\w\s]/g, '');
          if (mobile.length === 10) {
            setValue('mobileNumber', `1${mobile}`, { shouldValidate: true, shouldDirty: false });
          } else if (mobile.length === 11) {
            setValue('mobileNumber', `${mobile}`, { shouldValidate: true, shouldDirty: false });
          }
        } else
          setValue(key, props?.primaryGaurdian[key], { shouldValidate: true, shouldDirty: false });
      });
      if (isAdmin) clearErrors();
    } else if (!isAdmin) {
      [
        'userId',
        'firstName',
        'lastName',
        'email',
        'relation',
        'mobileNumber',
      ].forEach((key: any) => {
        if (key === 'userId')
          setValue('id', user ? user[key] : '', { shouldValidate: true, shouldDirty: false });
        else if (key === 'relation')
          setValue('relation', getRelation);
        else if (key === 'mobileNumber' && user[key] &&  user[key].length === 10) {
          setValue(key, `1${user[key]}`, { shouldValidate: false, shouldDirty: false });
        } else setValue(key, user ? user[key] : '', { shouldValidate: true, shouldDirty: false });
      });
      setValue('other', '', { shouldValidate: true, shouldDirty: false });
    }
  };

  useEffect(() => {
    setPrimaryGuardianDetails();
    //eslint-disable-next-line
  }, [guardians]);

  const handleConfirmSave = () => {
    if (!pendingFormData) return;

    function formatPhoneNumber(phoneNumberString: any) {
      const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
      const match = cleaned.match(/^(1)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        const intlCode = match[1] ? '+1 ' : '';
        return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      }
      return null;
    }

    const formData = pendingFormData;
    const payload = guardians
      .filter((guardian: any) => guardian.guardianCategory !== formData.guardianCategory)
      .concat({
        ...formData,
        id: formData?.id > 0 ? formData.id : null,
        guardianCategory: 'primary',
        other: formData?.relation === 'other' ? formData?.other : '',
        mobileNumber: formData?.mobileNumber
          ? formatPhoneNumber(formData?.mobileNumber.slice(1))
          : '',
      });

    props.onUpdateGuardian(payload, () => resetData(formData));

    setReadOnlyWarningDialog(false); // ✅ close dialog
  };

  //submit PG form
  const onSubmitForm = (formData: any) => {
    if (isValid && isDirty) {
      setPendingFormData(formData); // ✅ store data
      setReadOnlyWarningDialog(true); // ✅ open dialog
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  const resetData = (formData: any) => {
    const message1 = 'Primary guardian updated successfully.';
    const message2 = 'Primary guardian updated successfully. You can upload your picture.';
    toast(props.pictureAvailable ? message1 : message2, {
      type: 'success',
      icon: true,
    });
    reset(formData);
  };

  const handleSkipStep = () => {
    props.skipStep();
  };
  //return Primary Gaurdian Information component
  return (
    <Container component='main' maxWidth={'sm'} sx={{ padding: '0px !important' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{ mt: 1 }}
          component='form'
          noValidate
          onSubmit={handleSubmit((formData) => onSubmitForm(formData))}
        >
          <Box
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography
              variant='body1'
              gutterBottom
              sx={{ backgroundColor: '#FFFBF1', padding: '8px 8px 4px 8px' }}
            >
              <b>IMPORTANT:</b> Primary Guardian&apos;s cell phone and email will be our main
              communication channels.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='PGfirstName'>
               First Name*
              </InputLabel>
              <TextField
                id='PGfirstName'
                autoComplete='PGfirstname'
                placeholder="Enter guardian's first name"
                required
                fullWidth
                {...register('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName ? errors.firstName?.message?.toString() : ''}
                disabled={disableField}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='PGlastName'>
                Last Name*
              </InputLabel>
              <TextField
                id='PGlasttName'
                autoComplete='PGlastname'
                placeholder="Enter guardian's last name"
                required
                fullWidth
                {...register('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName ? errors.lastName?.message : ''}
                disabled={disableField}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='PGpatientRelation'>
                Relationship to the patient*
              </InputLabel>
              <TextField
                id='PGpatientRelation'
                autoComplete='PGpatientRelation'
                placeholder='Select Relation to the parent'
                select // tell TextField to render select
                required
                fullWidth
                {...register('relation')}
                error={!!errors.relation}
                helperText={errors.relation ? errors.relation?.message?.toString() : ''}
                value={watch('relation') ? watch('relation') : 'select_relation_to_the_child'}
                disabled={disableRelation}
              >
                <MenuItem
                  key={-1}
                  value={'select_relation_to_the_child'}
                  disabled={disableRelation}
                >
                  Select relation to the child
                </MenuItem>
                {GaurdianRelation.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='PGphoneNo'>
                Phone Number*
              </InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                onlyCountries={['us']}
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a phone number'}
                {...register('mobileNumber')}
                inputProps={{
                  helperText: errors.mobileNumber ? errors.mobileNumber?.message : '',
                  error: !!errors.mobileNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('mobileNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('mobileNumber') ? watch('mobileNumber') : null}
                disabled={getValues('mobileNumber') !== undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='PGemailAddress'>
                Email Address*
              </InputLabel>
              <TextField
                id='PGemailAddress'
                autoComplete='PGemailAddress'
                placeholder='Enter email address'
                required
                fullWidth
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email ? errors.email?.message?.toString() : ''}
                disabled={getValues('email') !== undefined}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            
          </Grid>
          {!isAdmin  && disableField && (
            <Grid item xs={12} sm={12} sx={{ mt: 2 }}>
              <Typography
                variant='body1'
                gutterBottom
                sx={{ backgroundColor: '#eef2ff', padding: '8px 8px 4px 8px' }}
              >
                <b>Note:</b> To make changes to the Primary Guardian information, please create a request under Account Management in the Account section.
              </Typography>    
            </Grid>
          )}
          
          <Grid container justifyContent='center'>
            <Grid item >
              <Button
                size='small'
                variant='contained'
                onClick={() => handleSkipStep()}
                sx={{
                  minWidth: 140,
                  mt: 2,
                  mb: 2,
                  marginRight: 2,
                }}
                disabled={nextButton}
              >
                NEXT
              </Button>
            </Grid>
            {isAdmin || !disableField ? (
              
              <Grid item>
                <LoadingButton
                  loading={isProfileDetailsFetched}
                  variant='contained'
                  // loadingPosition='start'
                  type='submit'
                  fullWidth
                  sx={{
                    minWidth: 140,
                    mt: 2,
                    mb: 2,
                  }}
                >
                Save
                </LoadingButton>
              </Grid>
            ): null}

           
          </Grid>
          

        </Box>
      </Box>
      <StyledDialog
        open={readOnlyWarningDialog}
        onClose={() => setReadOnlyWarningDialog(false)}
        maxWidth='xs' // ✅ smaller dialog
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Confirm Primary Guardian Information
              </Typography>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', px: 1 }}>
                Please review before saving. This information cannot be edited later.
              </Typography>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  mt: 1,
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 100,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                  }}
                  onClick={handleConfirmSave}
                  disabled={!pendingFormData}
                >
                  Confirm
                </Button>

                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 100,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                  }}
                  onClick={() => setReadOnlyWarningDialog(false)}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default PrimaryGaurdian;
